import { HttpClient, HttpContext, HttpErrorResponse, HttpResourceRef, httpResource } from '@angular/common/http';
import { Service, computed, inject, signal } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { Observable, map, tap } from 'rxjs';
import { environment } from '../../../../environments/environment';
import { ResponseApi } from '../../../core/dtos/response-api';
import { HANDLE_HTTP_ERROR_LOCALLY, SKIP_SUCCESS_ALERT } from '../../../core/http/http-context-tokens';
import { AuthService } from '../../../core/services/auth/auth.service';
import {
  AgendamentoResponse,
  ErroAgendamento,
  NovoAgendamento,
  PostoAgendavelResponse,
  ServicoAgendavelResponse,
} from '../models/agendamento.model';

const RECURSO = `${environment.apiUrl}/sai/cartaservico/MeusAgendamentos`;

/** O erro fica local: cada aba mostra a própria falha, sem o modal do interceptor. */
const ERRO_LOCAL = () => new HttpContext().set(HANDLE_HTTP_ERROR_LOCALLY, true);

/** Erro local e sem o alerta automático de sucesso: quem anuncia o resultado é a tela. */
const SILENCIOSO = () => ERRO_LOCAL().set(SKIP_SUCCESS_ALERT, true);

/** O id do posto que é o endereço principal do serviço — o mesmo em TODO serviço. */
const ID_POSTO_PRINCIPAL = 'principal';

const LIMITE = 'Muitas tentativas em pouco tempo. Aguarde alguns instantes e tente novamente.';
const SESSAO_EXPIRADA = 'Sua sessão expirou. Entre novamente.';

/**
 * O que o `POST MeusAgendamentos` carrega. Não há nome, CPF, usuário nem órgão: quem agenda é o
 * dono do token, no portal que chamou.
 */
export interface CorpoAgendamento {
  codServicoSrv: number;
  /** Vai sempre; `null` é o endereço principal do serviço. */
  codLocalAtendimentoServicoLas: number | null;
  /** `aaaa-mm-dd`, em texto: o back-end lê com formato exato. */
  dataAgendamento: string;
  /** `HH:mm`, em texto. */
  horarioAgendamento: string;
  /** Só quando o atendimento é para um dependente; ausente, é para o titular. */
  codDependenteDpd?: number;
}

/** A ação cujo erro `mapearErroAgendamento` traduz: o mesmo status muda de sentido com ela. */
export type AcaoAgendamento = 'agendar' | 'cancelar' | 'reagendar';

/**
 * Os agendamentos de quem está logado, em `api/sai/cartaservico/MeusAgendamentos`: os serviços
 * que dá para agendar (com os postos de cada um), a lista do cidadão, o agendamento, o
 * reagendamento e o cancelamento.
 *
 * As duas listas só são buscadas com sessão **e** depois de pedidas pela aba que as usa
 * (`carregarServicos()` na aba Agendamento, `carregar()` em Meus agendamentos): o serviço é da
 * raiz, e sem o pedido as duas sairiam para qualquer tela que o injetasse. Elas dependem do
 * usuário — cada login as refaz, e o logout as esquece.
 *
 * ⚠️ As gravações saem **silenciosas** (erro local e sem alerta de sucesso). A tela decide o que
 * fazer com cada erro (o 409 do horário tomado volta à etapa Data e Hora), e o cancelamento e o
 * reagendamento partem de um `<dialog>` aberto com `showModal()`, que deixaria o SweetAlert do
 * interceptor atrás do modal e sem clique.
 */
@Service()
export class AgendamentosService {
  private readonly http = inject(HttpClient);
  private readonly auth = inject(AuthService);

  private readonly usuario = toSignal(this.auth.currentUser, {
    initialValue: this.auth.currentUserValue,
  });

  private readonly servicosPedidos = signal(false);
  private readonly agendamentosPedidos = signal(false);

  private readonly servicosApi = httpResource<ResponseApi<ServicoAgendavelResponse[]>>(() => {
    // `undefined` é "nada a buscar": sem a guarda a chamada sairia sem token e voltaria 401.
    if (!this.servicosPedidos() || !this.usuario()?.token) return undefined;

    return { url: `${RECURSO}/servico`, context: ERRO_LOCAL() };
  });

  private readonly listaApi = httpResource<ResponseApi<AgendamentoResponse[]>>(() => {
    if (!this.agendamentosPedidos() || !this.usuario()?.token) return undefined;

    return { url: RECURSO, context: ERRO_LOCAL() };
  });

  readonly falhouServicos = computed(() => this.servicosApi.error() !== undefined);

  /**
   * Os serviços agendáveis, na ordem da API (alfabética), ou `null` sem sessão, antes do pedido,
   * na primeira carga ou em falha. Cada serviço já traz os postos: a etapa Posto não faz outra
   * chamada.
   */
  readonly servicos = computed<readonly ServicoAgendavelResponse[] | null>(() => dados(this.servicosApi));

  readonly falhou = computed(() => this.listaApi.error() !== undefined);

  /**
   * Os agendamentos, do mais recente para o mais antigo, ou `null` sem sessão, antes do pedido,
   * na primeira carga ou em falha. Durante o `reload()` de depois de uma gravação o valor
   * anterior fica — a lista não pisca "carregando".
   */
  readonly agendamentos = computed<readonly AgendamentoResponse[] | null>(() => dados(this.listaApi));

  /**
   * Pede os serviços agendáveis — a aba Agendamento chama ao abrir. Chamada de novo (a aba foi
   * reaberta), relê a lista: o órgão pode ter publicado um serviço ou encerrado um endereço.
   */
  carregarServicos(): void {
    if (this.servicosPedidos()) {
      this.servicosApi.reload();
    } else {
      this.servicosPedidos.set(true);
    }
  }

  /** Refaz a chamada dos serviços — é o "Tentar novamente" do aviso de falha. */
  recarregarServicos(): void {
    this.servicosApi.reload();
  }

  /**
   * Pede a lista de agendamentos — a aba Meus agendamentos chama ao abrir. Chamada de novo (a
   * aba foi reaberta), relê a lista: a situação muda sem ação do cidadão — o órgão conclui o
   * atendimento, o horário passa.
   */
  carregar(): void {
    if (this.agendamentosPedidos()) {
      this.listaApi.reload();
    } else {
      this.agendamentosPedidos.set(true);
    }
  }

  /** Refaz a chamada da lista — o "Tentar novamente", e a releitura depois de um erro de estado. */
  recarregar(): void {
    this.listaApi.reload();
  }

  /** Agenda e devolve o agendamento criado, com o protocolo. A lista do cidadão é relida. */
  agendar(novo: NovoAgendamento): Observable<AgendamentoResponse> {
    return this.http
      .post<ResponseApi<AgendamentoResponse>>(RECURSO, corpoDoAgendamento(novo), { context: SILENCIOSO() })
      .pipe(
        map((resposta) => resposta.data),
        tap(() => this.listaApi.reload()),
      );
  }

  /**
   * Troca a data e a hora de um agendamento, pelo protocolo — o serviço, o posto e o protocolo
   * continuam os mesmos. Devolve o agendamento já no horário novo; a lista do cidadão é relida.
   *
   * @param data `aaaa-mm-dd`, em texto.
   * @param horario `HH:mm`, em texto.
   */
  reagendar(protocolo: string, data: string, horario: string): Observable<AgendamentoResponse> {
    return this.http
      .post<ResponseApi<AgendamentoResponse>>(
        `${RECURSO}/${encodeURIComponent(protocolo.trim())}/reagendamento`,
        { dataAgendamento: data, horarioAgendamento: horario },
        { context: SILENCIOSO() },
      )
      .pipe(
        map((resposta) => resposta.data),
        tap(() => this.listaApi.reload()),
      );
  }

  /** Cancela pelo protocolo e libera a vaga. A lista do cidadão é relida. */
  cancelar(protocolo: string, motivo: string): Observable<AgendamentoResponse> {
    return this.http
      .post<ResponseApi<AgendamentoResponse>>(
        `${RECURSO}/${encodeURIComponent(protocolo.trim())}/cancelamento`,
        { desMotivo: motivo.trim() },
        { context: SILENCIOSO() },
      )
      .pipe(
        map((resposta) => resposta.data),
        tap(() => this.listaApi.reload()),
      );
  }
}

/**
 * O conteúdo do envelope, ou `null`.
 *
 * O `error()` é consultado ANTES do `value()` de propósito: em estado de erro o `value()`
 * **lança**, e a tela que mostra o aviso de falha é a mesma que lê esta lista.
 */
function dados<T>(recurso: HttpResourceRef<ResponseApi<T[]> | undefined>): readonly T[] | null {
  if (recurso.error() !== undefined) return null;

  return recurso.value()?.data ?? null;
}

/**
 * A escolha do wizard no contrato do `POST`.
 *
 * Função pura e exportada: é o ponto em que o contrato do back-end é honrado.
 *
 * - `codLocalAtendimentoServicoLas` vai **sempre**, `null` no endereço principal;
 * - `codDependenteDpd` só entra quando o cidadão escolhido é um dependente;
 * - nome, CPF, usuário e órgão nunca vão: o back-end os lê do token e do host.
 */
export function corpoDoAgendamento(novo: NovoAgendamento): CorpoAgendamento {
  const corpo: CorpoAgendamento = {
    codServicoSrv: novo.codServicoSrv,
    codLocalAtendimentoServicoLas: codLocalDe(novo.idPosto),
    dataAgendamento: novo.data,
    horarioAgendamento: novo.horario,
  };

  const codDependenteDpd = codDependenteDe(novo.idCidadao);

  if (codDependenteDpd !== null) corpo.codDependenteDpd = codDependenteDpd;

  return corpo;
}

/**
 * O código do dependente a partir do id da opção do campo "Cidadão": `'dependente-7'` → `7`.
 * `'titular'` — e qualquer id que não seja de dependente — é `null`: o atendimento é do titular.
 */
export function codDependenteDe(idCidadao: string): number | null {
  const codigo = /^dependente-(\d+)$/.exec(idCidadao)?.[1];

  return codigo === undefined ? null : Number(codigo);
}

/**
 * O id da opção do select de posto: `'principal'` para o endereço principal (código nulo) e
 * `'local-12'` para o endereço adicional de código 12.
 *
 * ⚠️ `'principal'` existe em todo serviço: o id só identifica o posto DENTRO de um serviço.
 */
export function idDoPosto(posto: Pick<PostoAgendavelResponse, 'codLocalAtendimentoServicoLas'>): string {
  const codigo = posto.codLocalAtendimentoServicoLas;

  return codigo === null ? ID_POSTO_PRINCIPAL : `local-${codigo}`;
}

/** O inverso de `idDoPosto`: `'local-12'` → `12`; `'principal'` → `null`. */
export function codLocalDe(idPosto: string): number | null {
  const codigo = /^local-(\d+)$/.exec(idPosto)?.[1];

  return codigo === undefined ? null : Number(codigo);
}

/**
 * `2026-10-12T09:40:00` → `12/10/2026 às 09:40` — o horário do agendamento e os limites do prazo.
 * Só texto: a data não passa pelo fuso do navegador.
 */
export function dataEHoraDe(iso: string): string {
  const [ano, mes, dia] = iso.slice(0, 10).split('-');

  return `${dia}/${mes}/${ano} às ${iso.slice(11, 16)}`;
}

/** As frases da tela para quando a API não manda a dela — uma por ação. */
const FRASES: Readonly<Record<AcaoAgendamento, { validacao: string; estado: string; indisponivel: string }>> = {
  agendar: {
    validacao: 'Não foi possível agendar com estes dados.',
    estado: 'Não foi possível concluir este agendamento.',
    indisponivel: 'Não foi possível concluir o agendamento agora. Tente novamente em instantes.',
  },
  cancelar: {
    validacao: 'Não foi possível cancelar o agendamento com estes dados.',
    estado: 'Este agendamento não pode mais ser cancelado.',
    indisponivel: 'Não foi possível cancelar o agendamento agora. Tente novamente em instantes.',
  },
  reagendar: {
    validacao: 'Não foi possível alterar o agendamento com estes dados.',
    estado: 'Este agendamento não pode mais ser alterado pelo portal.',
    indisponivel: 'Não foi possível alterar o agendamento agora. Tente novamente em instantes.',
  },
};

const HORARIO_TOMADO = 'Este horário acabou de ser preenchido. Escolha outro horário.';

/**
 * Traduz o erro HTTP de agendar, de reagendar ou de cancelar para o que a tela decide.
 *
 * A `message` da API é usada onde ela é a regra recusada (`400`, `403`, `409`, `422` e o `404`
 * do cancelamento e do reagendamento). Em `5xx` e sem resposta ela **nunca** é mostrada: num
 * erro de banco traz o texto do SQL Server.
 *
 * O `409` muda de sentido com a ação: ao agendar e ao reagendar é o horário que lotou (`vaga`) —
 * no reagendamento, também o órgão que mexeu no mesmo instante, e a tela faz o mesmo nos dois
 * casos: mostra a mensagem da API e relê o mês; ao cancelar, é só o órgão (`estado`).
 *
 * O `422` do reagendamento é o que encerra a tentativa: o prazo do portal terminou, o
 * agendamento mudou, ou o posto deixou de receber agendamento.
 */
export function mapearErroAgendamento(erro: unknown, acao: AcaoAgendamento): ErroAgendamento {
  const frases = FRASES[acao];
  const resposta = erro instanceof HttpErrorResponse ? erro : null;
  const corpo = (resposta?.error ?? null) as Partial<ResponseApi<unknown>> | null;

  const mensagemApi = typeof corpo?.message === 'string' ? corpo.message.trim() : '';

  // A mensagem costuma repetir a única violação; sem o filtro ela apareceria duas vezes.
  const detalhes = (Array.isArray(corpo?.errors) ? corpo.errors : [])
    .filter((item): item is string => typeof item === 'string' && item.trim().length > 0)
    .map((item) => item.trim())
    .filter((item) => item !== mensagemApi);

  const agendando = acao === 'agendar';

  switch (resposta?.status) {
    case 400:
      return { tipo: 'validacao', mensagem: mensagemApi || frases.validacao, detalhes };

    case 401:
      // O interceptor já encerrou a sessão; as abas mostram "Sua sessão foi encerrada.".
      return { tipo: 'sessao', mensagem: SESSAO_EXPIRADA, detalhes: [] };

    case 403:
      return {
        tipo: 'identificacao',
        mensagem:
          mensagemApi || 'Este serviço exige acesso pelo gov.br, que ainda não está disponível no portal.',
        detalhes: [],
      };

    case 404:
      // Ao agendar, o 404 é do serviço (despublicado, ou de outro portal): a frase é da tela.
      return agendando
        ? {
            tipo: 'nao-encontrado',
            mensagem: 'Este serviço não está mais disponível para agendamento.',
            detalhes: [],
          }
        : { tipo: 'nao-encontrado', mensagem: mensagemApi || 'Não encontramos este agendamento.', detalhes: [] };

    case 409:
      return acao === 'cancelar'
        ? {
            tipo: 'estado',
            mensagem:
              mensagemApi ||
              'O agendamento foi alterado enquanto você o cancelava. Atualize a lista e tente de novo.',
            detalhes: [],
          }
        : { tipo: 'vaga', mensagem: mensagemApi || HORARIO_TOMADO, detalhes: [] };

    case 422:
      return { tipo: 'estado', mensagem: mensagemApi || frases.estado, detalhes: [] };

    case 429:
      // O 429 vem em texto puro, fora do envelope: é do pipeline, antes do handler.
      return { tipo: 'limite', mensagem: LIMITE, detalhes: [] };

    default:
      // Status 0 (rede/CORS) e 5xx: o cidadão não tem o que corrigir, só tentar de novo.
      return { tipo: 'indisponivel', mensagem: frases.indisponivel, detalhes: [] };
  }
}
